import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectRobotutorMusicEnabled,
  selectRobotutorVoiceoverEnabled,
  selectSkipTutorialEnabled,
  toggleRobotutorMusic,
  toggleRobotutorVoiceover,
  toggleSkipTutorial
} from 'store/settings'
import {
  canSkipTutorialSelector,
  yearOneCompletionsCountSelector,
  YEAR_ONE_COMPLETIONS_FOR_SKIP_TUTORIAL
} from 'store/user/selectors'
import { updateProfile } from 'store/user/update'
import MusicNotesIcon from 'assets/icons/music-notes.svg'
import SkipForwardIcon from 'assets/icons/skip-forward.svg'
import VoiceOffIcon from 'assets/icons/voice-off.svg'
import GlobeIcon from 'assets/icons/globe.svg'

const LANGUAGE_OPTIONS = [
  { value: 'en', code: 'EN' },
  { value: 'tr', code: 'TR' },
  { value: 'cn', code: 'CN' },
  { value: 'es', code: 'ES' },
  { value: 'ko', code: 'KO' }
]

function OnOffToggle({ enabled, onChange, disabled, idPrefix }) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      id={idPrefix}
      className={`lobby__settings-toggle-btn ${
        enabled ? 'lobby__settings-toggle-btn--state-on' : 'lobby__settings-toggle-btn--state-off'
      }`}
      onClick={() => !disabled && onChange(!enabled)}
      disabled={disabled}
      aria-pressed={enabled}
    >
      {enabled ? t('settings.toggle_on') : t('settings.toggle_off')}
    </button>
  )
}

OnOffToggle.propTypes = {
  enabled: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  idPrefix: PropTypes.string.isRequired
}

OnOffToggle.defaultProps = {
  disabled: false
}

function LobbySettingsCard({ hasAiTeam }) {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const musicEnabled = useSelector(selectRobotutorMusicEnabled)
  const voiceoverEnabled = useSelector(selectRobotutorVoiceoverEnabled)
  const skipTutorialEnabled = useSelector(selectSkipTutorialEnabled)
  const canSkipTutorial = useSelector(canSkipTutorialSelector)
  const yearOneCompletions = useSelector(yearOneCompletionsCountSelector)

  const skipEffective = skipTutorialEnabled && hasAiTeam && canSkipTutorial
  const skipDisabled = !hasAiTeam || !canSkipTutorial
  const remainingYearOneCompletions = Math.max(
    0,
    YEAR_ONE_COMPLETIONS_FOR_SKIP_TUTORIAL - yearOneCompletions
  )
  const skipTutorialLockedKey =
    remainingYearOneCompletions === 1
      ? 'lobby.skip_tutorial_locked_one'
      : 'lobby.skip_tutorial_locked_other'

  const handleLanguageChange = (event) => {
    const locale = event.target.value
    dispatch(updateProfile({ locale }))
    i18n.changeLanguage(locale)
  }

  const baseLang = i18n.language?.split('-')[0]
  const languageValue = LANGUAGE_OPTIONS.some((o) => o.value === baseLang) ? baseLang : 'en'

  return (
    <div className="lobby__settings-card">
      <h2 className="lobby__settings-title">{t('settings.title')}</h2>

      <div className="lobby__settings-toggles">
        <div className="lobby__settings-row">
          <div className="lobby__settings-row-main">
            <span className="lobby__settings-icon" aria-hidden>
              <img src={MusicNotesIcon} alt="" className="lobby__settings-icon-img" />
            </span>
            <span className="lobby__settings-label">{t('settings.music')}</span>
          </div>
          <OnOffToggle
            enabled={musicEnabled}
            onChange={(next) => dispatch(toggleRobotutorMusic(next))}
            idPrefix="lobby-settings-music"
          />
        </div>

        <div className="lobby__settings-row">
          <div className="lobby__settings-row-main">
            <span className="lobby__settings-icon" aria-hidden>
              <img src={VoiceOffIcon} alt="" className="lobby__settings-icon-img" />
            </span>
            <span className="lobby__settings-label">{t('settings.voice')}</span>
          </div>
          <OnOffToggle
            enabled={voiceoverEnabled}
            onChange={(next) => dispatch(toggleRobotutorVoiceover(next))}
            idPrefix="lobby-settings-voice"
          />
        </div>

        <div
          className={`lobby__settings-row ${
            skipDisabled ? 'lobby__settings-row--skip-disabled' : ''
          }`}
        >
          <div className="lobby__settings-row-main">
            <span className="lobby__settings-icon" aria-hidden>
              <img src={SkipForwardIcon} alt="" className="lobby__settings-icon-img" />
            </span>
            <div className="lobby__settings-label-block">
              <span className="lobby__settings-label">{t('lobby.skip_tutorial')}</span>
              {!canSkipTutorial && (
                <span className="lobby__settings-hint">
                  {t(skipTutorialLockedKey, { count: remainingYearOneCompletions })}
                </span>
              )}
            </div>
          </div>
          <OnOffToggle
            enabled={skipEffective}
            onChange={(next) => dispatch(toggleSkipTutorial(next))}
            disabled={skipDisabled}
            idPrefix="lobby-settings-skip-tutorial"
          />
        </div>
      </div>

      <div className="lobby__settings-divider" aria-hidden />

      <div className="lobby__settings-row lobby__settings-row--language">
        <div className="lobby__settings-row-main">
          <span className="lobby__settings-icon" aria-hidden>
            <img src={GlobeIcon} alt="" className="lobby__settings-icon-img" />
          </span>
          <span className="lobby__settings-label">{t('settings.language_label')}</span>
        </div>
        <div className="lobby__settings-select-wrap">
          <select
            className="lobby__settings-select"
            value={languageValue}
            onChange={handleLanguageChange}
            aria-label={t('settings.language')}
          >
            {LANGUAGE_OPTIONS.map(({ value, code }) => (
              <option key={value} value={value}>
                {code}
              </option>
            ))}
          </select>
          <span className="lobby__settings-select-chevron" aria-hidden>
            <i className="fa-solid fa-chevron-down" />
          </span>
        </div>
      </div>
    </div>
  )
}

LobbySettingsCard.propTypes = {
  hasAiTeam: PropTypes.bool.isRequired
}

export default LobbySettingsCard
